import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MapPin, Upload, DollarSign, Users, Plus, X, Image as ImageIcon, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MapComponent from '../components/MapComponent';
import StarRating from '../components/StarRating';
import { locationsAPI } from '../utils/api';
import MarkdownHint from '../components/MarkdownHint';
import { toast } from 'sonner';

/**
 * Browser-side image compression. Downscales to fit `maxDim` and re-encodes
 * as JPEG at the given quality. Returns a base64 data-URL.
 *
 * Why: phone photos are 3–10 MB each. With 8 photos we'd blow past MongoDB's
 * 16 MB BSON document limit and the server would reject the insert with
 * `DocumentTooLarge`. ~200 KB per photo keeps us safe.
 */
function compressImage(file, maxDim = 1600, quality = 0.75) {
  return new Promise((resolve, reject) => {
    // SVG / unknown content: keep as-is.
    if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
      return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        URL.revokeObjectURL(url);
        resolve(dataUrl);
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

const AddLocation = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditMode = Boolean(editId);
  const [stripeConnected, setStripeConnected] = useState(false);
  const [checkingStripe, setCheckingStripe] = useState(true);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    fullDescription: '',
    pricing_mode: 'flat',  // 'flat' or 'per_unit'
    price: '',
    price_per_adult: '',
    price_per_child: '',
    price_per_car: '',
    price_per_tent: '',
    price_per_cabin: '',
    price_per_cabin_unit: '',
    cabin_pricing_mode: 'per_person',
    maxCapacity: '',
    terrain: '',
    accessibility: '',
    facilities: [],
    latitude: 49.8175,
    longitude: 15.4730,
    // Arrival instructions for guests (included in confirmation emails)
    arrival_instructions: '',
    arrival_image: '',
    arrival_images: [],
    // House rules from owner (free text, public)
    owner_rules: '',
    // Owner explicitly disallows caravans/motorhomes (badge propagates to listing)
    no_caravans: false,
    // Access to the plot
    open_access: true,
    open_access_instructions: '',
    // Campfire allowed by owner (Yes/No) – legal disclaimer shown to guests
    fire_allowed: null,
    // Check-in / check-out times
    check_in_time: '15:00',
    check_out_time: '11:00',
    // Up to 5 nearby attractions: [{title, url}]
    nearby_attractions: [
      { title: '', url: '' },
      { title: '', url: '' },
      { title: '', url: '' },
      { title: '', url: '' },
      { title: '', url: '' },
    ],
    // NEW FIELDS
    mobile_signal: 'Střední',
    stargazing_rating: 3,
    wildlife_rating: 3,
    privacy_rating: 3,
    view_rating: 3,
    sounds_rating: 3,
    parking_type: 'on_site',
    parking_distance_m: '',
    tourist_traffic: 'Střední',
    photos: [],
    // Required: host must explicitly choose – null means not yet chosen
    instant_book: null,
    rules: {
      pets_allowed: 'ne',
      quiet_hours: false,
      quiet_hours_from: '',
      quiet_hours_to: '',
      smoking_allowed: false,
      alcohol_allowed: true,
    },
  });

  const [newFacility, setNewFacility] = useState('');
  const [photoFiles, setPhotoFiles] = useState([]);
  const [hasUserPickedSpot, setHasUserPickedSpot] = useState(false);
  const [errors, setErrors] = useState({});

  const clearError = (key) => setErrors((p) => ({ ...p, [key]: undefined }));

  // Check Stripe Connect status on mount
  useEffect(() => {
    checkStripeStatus();
  }, []);

  // Load existing location for edit mode
  useEffect(() => {
    if (!editId) return;
    locationsAPI.getById(editId)
      .then(({ data }) => {
        setFormData((prev) => ({
          ...prev,
          name: data.name || '',
          category: data.category || '',
          description: data.description || '',
          fullDescription: data.full_description || '',
          pricing_mode: data.pricing_mode || 'flat',
          price: data.price?.toString() || '',
          price_per_adult: data.price_per_adult?.toString() || '',
          price_per_child: data.price_per_child?.toString() || '',
          price_per_car: data.price_per_car?.toString() || '',
          price_per_tent: data.price_per_tent?.toString() || '',
          price_per_cabin: data.price_per_cabin?.toString() || '',
          price_per_cabin_unit: data.price_per_cabin_unit?.toString() || '',
          cabin_pricing_mode: (parseFloat(data.price_per_cabin_unit) > 0) ? 'combined' : 'per_person',
          maxCapacity: data.max_capacity?.toString() || '',
          terrain: data.terrain || '',
          accessibility: data.accessibility || '',
          facilities: data.facilities || [],
          latitude: data.latitude ?? 49.8175,
          longitude: data.longitude ?? 15.4730,
          arrival_instructions: data.arrival_instructions || '',
          arrival_image: data.arrival_image || '',
          arrival_images: data.arrival_images || (data.arrival_image ? [data.arrival_image] : []),
          owner_rules: data.owner_rules || '',
          no_caravans: data.no_caravans || false,
          open_access: data.open_access ?? true,
          open_access_instructions: data.open_access_instructions || '',
          fire_allowed: typeof data.fire_allowed === 'boolean' ? data.fire_allowed : null,
          check_in_time: data.check_in_time || '15:00',
          check_out_time: data.check_out_time || '11:00',
          nearby_attractions: (() => {
            const arr = Array.isArray(data.nearby_attractions) ? [...data.nearby_attractions] : [];
            while (arr.length < 5) arr.push({ title: '', url: '' });
            return arr.slice(0, 5);
          })(),
          mobile_signal: data.mobile_signal || 'Střední',
          stargazing_rating: data.stargazing_rating || 3,
          wildlife_rating: data.wildlife_rating || 3,
          privacy_rating: data.privacy_rating || 3,
          view_rating: data.view_rating || 3,
          sounds_rating: data.sounds_rating || 3,
          parking_type: data.parking_type || 'on_site',
          parking_distance_m: data.parking_distance_m ? data.parking_distance_m.toString() : '',
          tourist_traffic: data.tourist_traffic || 'Střední',
          photos: data.photos || [],
          instant_book: data.instant_book ?? true,
          rules: data.rules || prev.rules,
        }));
        // Mirror existing photos into the photoFiles state so the preview grid
        // and "at least 1 photo" validation work in edit mode.
        if (Array.isArray(data.photos)) setPhotoFiles(data.photos);
        setHasUserPickedSpot(true);
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || 'Lokace nenalezena');
        navigate('/dashboard');
      });
  }, [editId, navigate]);

  const checkStripeStatus = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
      const response = await fetch(`${API_URL}/payments/connect/account-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStripeStatus(data);
        // Only treat as fully ready when Stripe has accepted documents and enabled charges.
        setStripeConnected(data.connected && data.details_submitted && data.charges_enabled);
      }
    } catch (error) {
      console.error('Error checking Stripe status:', error);
    } finally {
      setCheckingStripe(false);
    }
  };

  const handleConnectStripe = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
      const token = localStorage.getItem('token');
      const origin = window.location.origin;

      const response = await fetch(`${API_URL}/payments/connect/create-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          return_url: `${origin}/add-location?stripe_return=true`,
          refresh_url: `${origin}/add-location`
        })
      });

      if (!response.ok) {
        throw new Error('Chyba při vytváření Stripe účtu');
      }

      const data = await response.json();
      
      // Redirect to Stripe onboarding
      window.location.href = data.url;
    } catch (error) {
      console.error('Stripe connect error:', error);
      toast.error('Chyba při propojování bankovního účtu');
    }
  };

  // Wipe saved stripe_account_id (use when stripe shows "sandbox" because a
  // stale test-mode account is stored from earlier development sessions).
  const handleResetStripe = async () => {
    if (!window.confirm('Opravdu odpojit aktuální Stripe účet? Budete muset projít registrací znovu.')) return;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/payments/connect/reset-account`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('reset failed');
      toast.success('Stripe účet odpojen. Klikněte znovu na Propojit účet.');
      // re-check status so UI updates
      setStripeConnected(false);
    } catch (e) {
      console.error(e);
      toast.error('Nepodařilo se odpojit Stripe účet');
    }
  };

  const categories = [
    { value: 'Dodávka', label: '🚐 Dodávka' },
    { value: '2WD vůz', label: '🚗 2WD vůz', desc: 'osobní auto' },
    { value: 'Offroad', label: '🚙 Offroad' },
    { value: 'Pěšky', label: '🚶 Pěšky' },
  ];

  const terrains = [
    { value: 'Lehký terén', label: 'Lehký terén' },
    { value: 'Střední terén', label: 'Střední terén' },
    { value: 'Náročný terén', label: 'Náročný terén' },
  ];

  const mobileSignalOptions = [
    { value: 'Silný', label: 'Silný' },
    { value: 'Střední', label: 'Střední' },
    { value: 'Žádný', label: 'Žádný' },
  ];

  const touristTrafficOptions = [
    { value: 'Nízký', label: 'Nízký' },
    { value: 'Střední', label: 'Střední' },
    { value: 'Vysoký', label: 'Vysoký' },
  ];

  // Category is stored as comma-separated string (e.g. "Dodávka, 2WD vůz")
  const categoryList = formData.category
    ? formData.category.split(',').map((s) => s.trim()).filter(Boolean)
    : [];

  const toggleCategory = (value) => {
    const current = new Set(categoryList);
    if (current.has(value)) current.delete(value);
    else current.add(value);
    setFormData({ ...formData, category: Array.from(current).join(', ') });
    if (errors.category && current.size > 0) {
      setErrors((p) => ({ ...p, category: undefined }));
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + photoFiles.length > 8) {
      toast.error('Můžete nahrát maximálně 8 fotografií');
      return;
    }

    // Compress every image to max 1600px wide JPEG @ 0.75 quality.
    // Reduces a typical 3 MB phone photo to ~150-300 KB, so 8 photos fit
    // comfortably under MongoDB's 16 MB document limit.
    Promise.all(files.map((f) => compressImage(f, 1600, 0.75))).then((results) => {
      setPhotoFiles([...photoFiles, ...results]);
      setFormData({
        ...formData,
        photos: [...formData.photos, ...results],
      });
      clearError('photos');
    }).catch((err) => {
      console.error('photo compress failed', err);
      toast.error('Chyba při zpracování fotek');
    });
  };

  const handleRemovePhoto = (index) => {
    setPhotoFiles(photoFiles.filter((_, i) => i !== index));
    setFormData({
      ...formData,
      photos: formData.photos.filter((_, i) => i !== index),
    });
  };

  const handleAddFacility = () => {
    if (newFacility.trim()) {
      setFormData({
        ...formData,
        facilities: [...formData.facilities, newFacility.trim()],
      });
      setNewFacility('');
      clearError('facilities');
    }
  };

  const handleRemoveFacility = (index) => {
    setFormData({
      ...formData,
      facilities: formData.facilities.filter((_, i) => i !== index),
    });
  };

  const handleMapClick = (coords) => {
    setFormData((prev) => ({
      ...prev,
      latitude: coords.latitude,
      longitude: coords.longitude,
    }));
    setHasUserPickedSpot(true);
    clearError('map');
    toast.success(`Lokace vybrána: ${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}`);
  };

  const handleArrivalImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const current = formData.arrival_images || [];
    if (files.length + current.length > 3) {
      toast.error('Maximálně 3 obrázky pro pokyny k příjezdu');
      return;
    }
    Promise.all(files.map((f) => compressImage(f, 1600, 0.75))).then((results) => {
      setFormData((prev) => ({
        ...prev,
        arrival_images: [...(prev.arrival_images || []), ...results].slice(0, 3),
      }));
    }).catch((err) => {
      console.error('arrival image compress failed', err);
      toast.error('Chyba při zpracování obrázku');
    });
  };

  const removeArrivalImage = (idx) => {
    setFormData((prev) => ({
      ...prev,
      arrival_images: (prev.arrival_images || []).filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if user is logged in
    const token = localStorage.getItem('token');
    const currentUser = localStorage.getItem('currentUser');
    if (!token || !currentUser) {
      toast.error('Pro přidání místa se musíte přihlásit');
      navigate('/login');
      return;
    }

    // Build full errors map so user sees ALL missing fields at once (not just one toast)
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = 'Vyplňte název místa';
    if (categoryList.length === 0) newErrors.category = 'Vyberte alespoň jednu dostupnost (Dodávka / 2WD vůz / Offroad / Pěšky)';
    if (!formData.description?.trim()) newErrors.description = 'Vyplňte krátký popis';
    if (!formData.fullDescription?.trim()) newErrors.fullDescription = 'Vyplňte podrobný popis';
    if (!formData.terrain) newErrors.terrain = 'Vyberte typ terénu';
    if (!formData.maxCapacity || parseInt(formData.maxCapacity) <= 0) newErrors.maxCapacity = 'Zadejte maximální počet osob';
    if (formData.facilities.length === 0) newErrors.facilities = 'Přidejte alespoň jedno vybavení (napište a stiskněte +)';
    if (photoFiles.length === 0) newErrors.photos = 'Nahrajte alespoň 1 fotografii';
    if (formData.instant_book === null || formData.instant_book === undefined) {
      newErrors.instant_book = 'Vyberte způsob rezervace (okamžitá / s potvrzením)';
    }
    if (formData.fire_allowed === null || formData.fire_allowed === undefined) {
      newErrors.fire_allowed = 'Zvolte, zda je na pozemku povoleno rozdělání ohně';
    }
    if (!hasUserPickedSpot) newErrors.map = 'Klikněte na mapě a vyberte přesnou polohu';

    if (formData.pricing_mode === 'flat') {
      if (!formData.price || parseFloat(formData.price) <= 0) {
        newErrors.price = 'Zadejte jednotnou cenu za noc';
      }
    } else {
      const hasAny = ['price_per_adult', 'price_per_child', 'price_per_car', 'price_per_tent', 'price_per_cabin', 'price_per_cabin_unit']
        .some((k) => parseFloat(formData[k]) > 0);
      if (!hasAny) {
        newErrors.price = 'Zadejte alespoň jednu detailní cenu (osoba/dítě/auto/stan/chata)';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const count = Object.keys(newErrors).length;
      toast.error(`Zbývá vyplnit ${count} ${count === 1 ? 'pole' : count < 5 ? 'pole' : 'polí'} – zvýrazněna červeně`);
      // Scroll to the first error
      setTimeout(() => {
        const firstEl = document.querySelector('[data-error="true"]');
        if (firstEl) firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
      return;
    }
    setErrors({});

    try {
      const user = JSON.parse(currentUser);
      
      // For backward compat, when per_unit we still send the adult price as the base "price" (used for sorting/listing).
      const basePrice = formData.pricing_mode === 'flat'
        ? parseFloat(formData.price)
        : parseFloat(formData.price_per_adult || formData.price_per_tent || formData.price_per_car || 0);

      // Prepare location data for API
      const locationData = {
        name: formData.name,
        category: formData.category,
        type: formData.category,
        description: formData.description,
        full_description: formData.fullDescription,
        pricing_mode: formData.pricing_mode,
        price: basePrice,
        price_unit: "Kč / noc",
        price_per_adult: parseFloat(formData.price_per_adult) || 0,
        price_per_child: parseFloat(formData.price_per_child) || 0,
        price_per_car: parseFloat(formData.price_per_car) || 0,
        price_per_tent: parseFloat(formData.price_per_tent) || 0,
        price_per_cabin: parseFloat(formData.price_per_cabin) || 0,
        // Only send unit price when host explicitly picked the combined model
        price_per_cabin_unit: formData.cabin_pricing_mode === 'combined'
          ? (parseFloat(formData.price_per_cabin_unit) || 0)
          : 0,
        latitude: formData.latitude,
        longitude: formData.longitude,
        facilities: formData.facilities,
        max_capacity: parseInt(formData.maxCapacity),
        terrain: formData.terrain,
        accessibility: formData.category,
        mobile_signal: formData.mobile_signal,
        stargazing_rating: formData.stargazing_rating,
        wildlife_rating: formData.wildlife_rating,
        privacy_rating: formData.privacy_rating,
        view_rating: formData.view_rating,
        sounds_rating: formData.sounds_rating,
        parking_type: formData.parking_type,
        parking_distance_m: formData.parking_type === 'off_site'
          ? (parseInt(formData.parking_distance_m) || 0)
          : 0,
        tourist_traffic: formData.tourist_traffic,
        photos: formData.photos,
        arrival_instructions: formData.arrival_instructions || null,
        arrival_image: formData.arrival_images?.[0] || formData.arrival_image || null,
        arrival_images: formData.arrival_images || [],
        owner_rules: formData.owner_rules || null,
        no_caravans: !!formData.no_caravans,
        open_access: !!formData.open_access,
        open_access_instructions: formData.open_access ? null : (formData.open_access_instructions || null),
        fire_allowed: typeof formData.fire_allowed === 'boolean' ? formData.fire_allowed : null,
        check_in_time: formData.check_in_time || null,
        check_out_time: formData.check_out_time || null,
        nearby_attractions: (formData.nearby_attractions || [])
          .filter((n) => (n.title && n.title.trim()) || (n.url && n.url.trim()))
          .map((n) => ({
            title: (n.title || '').slice(0, 150).trim(),
            url: (n.url || '').trim(),
          })),
        rules: formData.rules,
        instant_book: formData.instant_book,
        owner: {
          name: user.name,
          phone: user.phone,
          email: user.email,
          user_id: user.id
        }
      };

      // Import API helper (kept for backward-compat; locationsAPI is now imported above)
      toast.loading(isEditMode ? 'Ukládám změny...' : 'Přidávání místa...');
      if (isEditMode) {
        await locationsAPI.update(editId, locationData);
        toast.dismiss();
        toast.success('Změny uloženy ✓');
      } else {
        await locationsAPI.create(locationData);
        toast.dismiss();
        toast.success('Místo bylo úspěšně přidáno! Po kontrole bude zveřejněno.');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.dismiss();
      console.error('Error saving location:', error);
      toast.error(error.response?.data?.detail || 'Chyba při ukládání místa');
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-32 sm:pt-44 lg:pt-52 pb-4 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-2xl">Přidat místo</h1>
        <p className="text-xl text-white/90 drop-shadow-lg">
          Sdílejte své místo s komunitou kempařů
        </p>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Stripe Connect Check */}
        {checkingStripe && (
          <Card className="shadow-xl bg-white/95 mb-6">
            <CardContent className="p-8 text-center">
              <p>Kontroluji propojení účtu...</p>
            </CardContent>
          </Card>
        )}
        
        {!checkingStripe && (() => {
          const s = stripeStatus || {};
          const fullyReady = s.connected && s.details_submitted && s.charges_enabled;

          // Case 1: fully ready - green banner
          if (fullyReady) {
            return (
              <Card className="shadow-xl bg-green-50 mb-6" data-testid="stripe-ready-card">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <p className="text-green-800 font-semibold">✓ Účet propojen a ověřen</p>
                </CardContent>
              </Card>
            );
          }

          // Case 2: account exists but verification pending or incomplete
          if (s.connected) {
            const docsSubmitted = !!s.details_submitted;
            return (
              <Card className="shadow-xl bg-amber-50 mb-6 border-2 border-amber-500" data-testid="stripe-incomplete-card">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <AlertCircle className="w-8 h-8 text-amber-600 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-3">
                        {docsSubmitted ? 'Stripe ověřuje vaše dokumenty' : 'Dokončete nastavení Stripe účtu'}
                      </h3>
                      <p className="text-amber-800 mb-2">
                        {docsSubmitted ? (
                          <>Dokumenty jste odeslal, Stripe je teď <strong>ověřuje</strong> (obvykle pár hodin, max 1–2 dny). Můžete vyplnit místo a uložit ho jako koncept – platby se aktivují automaticky po ověření.</>
                        ) : (
                          <>Účet je vytvořený, ale musíte ještě <strong>dokončit registraci</strong> ve Stripe – občanku, IBAN, telefon, adresu, datum narození.</>
                        )}
                      </p>
                      <p className="text-sm text-amber-700 mb-4">
                        Stav účtu:&nbsp;
                        <span className="font-mono">connected={String(s.connected)}, details_submitted={String(s.details_submitted)}, charges_enabled={String(s.charges_enabled)}</span>
                      </p>
                      <Button onClick={handleConnectStripe} className="bg-amber-600 hover:bg-amber-700" data-testid="continue-stripe-btn">
                        <ExternalLink className="w-5 h-5 mr-2" />
                        {docsSubmitted ? 'Zkontrolovat stav ve Stripe' : 'Pokračovat v nastavení'}
                      </Button>
                      <button
                        type="button"
                        onClick={handleResetStripe}
                        className="block mt-3 text-xs text-gray-500 hover:text-red-600 underline"
                        data-testid="reset-stripe-btn"
                      >
                        Začít znovu (smazat účet a vytvořit nový)
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }

          // Case 3: no account yet
          return (
            <Card className="shadow-xl bg-orange-50 mb-6 border-2 border-orange-500" data-testid="stripe-not-connected-card">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-8 h-8 text-orange-600 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-3">Propojte bankovní účet</h3>
                    <p className="text-orange-800 mb-4">
                      Před přidáním místa propojte účet. Budete dostávat <strong>85% z rezervací</strong>.
                    </p>
                    <Button onClick={handleConnectStripe} className="bg-orange-600 hover:bg-orange-700" data-testid="connect-stripe-btn">
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Propojit účet
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl">Informace o místě</CardTitle>
            <p className="text-gray-600">Vyplňte podrobnosti o vašem kempovacím místě</p>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              {/* Basic Info */}
              <div data-error={!!errors.name}>
                <Label htmlFor="name">Název místa *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Např. Louka u jezera"
                  className={`mt-1 ${errors.name ? 'border-red-500 focus-visible:ring-red-400' : ''}`}
                  value={formData.name}
                  onChange={(e) => { setFormData({ ...formData, name: e.target.value }); if (e.target.value) clearError('name'); }}
                  required
                />
                {errors.name && <p className="text-xs text-red-600 mt-1">⚠ {errors.name}</p>}
              </div>

              <div data-error={!!errors.category} data-testid="category-buttons">
                <Label>Dostupnost * <span className="text-xs text-gray-500 font-normal">(vyberte alespoň jeden způsob přístupu, klidně více)</span></Label>
                <div className={`mt-2 flex flex-wrap gap-2 p-2 rounded-lg ${errors.category ? 'ring-2 ring-red-400 bg-red-50' : ''}`}>
                  {categories.map((opt) => {
                    const active = categoryList.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggleCategory(opt.value)}
                        data-testid={`category-btn-${opt.value}`}
                        aria-pressed={active}
                        className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                          active
                            ? 'bg-orange-600 text-white border-orange-600 shadow-sm'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400 hover:bg-orange-50'
                        }`}
                      >
                        {opt.label}
                        {opt.desc && !active && <span className="text-xs text-gray-500 ml-1">({opt.desc})</span>}
                      </button>
                    );
                  })}
                </div>
                {categoryList.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    Vybráno: <strong>{categoryList.length}</strong> {categoryList.length === 1 ? 'možnost' : 'možností'}
                  </p>
                )}
                {errors.category && <p className="text-xs text-red-600 mt-1">⚠ {errors.category}</p>}
              </div>

              <div data-error={!!errors.description}>
                <Label htmlFor="description">Krátký popis *</Label>
                <Textarea
                  id="description"
                  placeholder="Stručně popište místo (max 100 znaků)"
                  rows={2}
                  className={`mt-1 ${errors.description ? 'border-red-500 focus-visible:ring-red-400' : ''}`}
                  maxLength={100}
                  value={formData.description}
                  onChange={(e) => { setFormData({ ...formData, description: e.target.value }); if (e.target.value) clearError('description'); }}
                  required
                />
                {errors.description && <p className="text-xs text-red-600 mt-1">⚠ {errors.description}</p>}
              </div>

              <div data-error={!!errors.fullDescription}>
                <Label htmlFor="fullDescription">Podrobný popis *</Label>
                <MarkdownHint />
                <Textarea
                  id="fullDescription"
                  placeholder="Detailně popište místo, okolí, přístup. Použijte **tučně** pro zvýraznění důležitých částí..."
                  rows={5}
                  className={`mt-1 ${errors.fullDescription ? 'border-red-500 focus-visible:ring-red-400' : ''}`}
                  value={formData.fullDescription}
                  onChange={(e) => { setFormData({ ...formData, fullDescription: e.target.value }); if (e.target.value) clearError('fullDescription'); }}
                  required
                />
                {errors.fullDescription && <p className="text-xs text-red-600 mt-1">⚠ {errors.fullDescription}</p>}
              </div>

              {/* Pricing & Capacity */}
              <div className="border-t pt-6 mt-6">
                <Label className="text-base font-semibold">Cenový model *</Label>
                <p className="text-sm text-gray-600 mb-3">
                  Vyberte, jak chcete účtovat pobyt. Cena <strong>jednotná za noc</strong>, nebo <strong>detailní ceník</strong> (osoba / dítě / auto / stan).
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <label
                    className={`cursor-pointer border-2 rounded-xl p-4 transition-all ${
                      formData.pricing_mode === 'flat'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="pricing-mode-flat"
                  >
                    <input
                      type="radio"
                      name="pricing_mode"
                      checked={formData.pricing_mode === 'flat'}
                      onChange={() => setFormData({ ...formData, pricing_mode: 'flat' })}
                      className="sr-only"
                    />
                    <div className="font-semibold mb-1">💰 Jednotná cena za noc</div>
                    <p className="text-xs text-gray-600">Jedna cena nezávisí na počtu hostů.</p>
                  </label>
                  <label
                    className={`cursor-pointer border-2 rounded-xl p-4 transition-all ${
                      formData.pricing_mode === 'per_unit'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="pricing-mode-per-unit"
                  >
                    <input
                      type="radio"
                      name="pricing_mode"
                      checked={formData.pricing_mode === 'per_unit'}
                      onChange={() => setFormData({ ...formData, pricing_mode: 'per_unit' })}
                      className="sr-only"
                    />
                    <div className="font-semibold mb-1">📋 Detailní ceník</div>
                    <p className="text-xs text-gray-600">Cena za osobu, dítě, auto, stan – sčítá se podle rezervace.</p>
                  </label>
                </div>

                {formData.pricing_mode === 'flat' ? (
                  <div data-error={!!errors.price}>
                    <Label htmlFor="price">Cena za noc (Kč) *</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="price"
                        type="number"
                        placeholder="350"
                        className={`pl-10 ${errors.price ? 'border-red-500 focus-visible:ring-red-400' : ''}`}
                        value={formData.price}
                        onChange={(e) => { setFormData({ ...formData, price: e.target.value }); if (parseFloat(e.target.value) > 0) clearError('price'); }}
                      />
                    </div>
                    {errors.price && <p className="text-xs text-red-600 mt-1">⚠ {errors.price}</p>}
                  </div>
                ) : (
                  <div data-error={!!errors.price}>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { key: 'price_per_adult', label: 'Cena za dospělého (Kč/noc)', placeholder: '250' },
                        { key: 'price_per_child', label: 'Cena za dítě (Kč/noc)', placeholder: '100' },
                        { key: 'price_per_car', label: 'Cena za auto (Kč/noc)', placeholder: '100' },
                        { key: 'price_per_tent', label: 'Cena za stan (Kč/noc)', placeholder: '150' },
                      ].map(({ key, label, placeholder }) => (
                        <div key={key}>
                          <Label htmlFor={key}>{label}</Label>
                          <Input
                            id={key}
                            type="number"
                            placeholder={placeholder}
                            className={`mt-1 ${errors.price ? 'border-red-500' : ''}`}
                            value={formData[key]}
                            onChange={(e) => { setFormData({ ...formData, [key]: e.target.value }); if (parseFloat(e.target.value) > 0) clearError('price'); }}
                            data-testid={`input-${key}`}
                          />
                        </div>
                      ))}

                      {/* Chata / maringotka / teepee – choose pricing model */}
                      <div className="col-span-2 mt-2 border-t border-orange-200 pt-4" data-testid="cabin-pricing-block">
                        <Label className="font-semibold">🏠 Chata / maringotka / teepee <span className="text-gray-500 font-normal">(nepovinné)</span></Label>
                        <p className="text-xs text-gray-600 mb-2 mt-1">Jak chcete tento typ ubytování účtovat?</p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                          {[
                            { val: 'per_person', label: 'Cena za osobu', hint: 'Např. 300 Kč × počet osob × počet nocí' },
                            { val: 'combined', label: 'Pevná cena za objekt + cena za osobu', hint: 'Např. 1000 Kč za objekt + 150 Kč × osob × nocí' },
                          ].map((opt) => (
                            <label
                              key={opt.val}
                              className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                                (formData.cabin_pricing_mode || 'per_person') === opt.val
                                  ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200'
                                  : 'border-gray-200 hover:border-orange-300'
                              }`}
                              data-testid={`cabin-mode-${opt.val}`}
                            >
                              <input
                                type="radio"
                                name="cabin_pricing_mode"
                                value={opt.val}
                                className="mr-2"
                                checked={(formData.cabin_pricing_mode || 'per_person') === opt.val}
                                onChange={() => setFormData({ ...formData, cabin_pricing_mode: opt.val })}
                              />
                              <span className="font-semibold text-sm">{opt.label}</span>
                              <p className="text-xs text-gray-500 mt-0.5 pl-6">{opt.hint}</p>
                            </label>
                          ))}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {(formData.cabin_pricing_mode === 'combined') && (
                            <div>
                              <Label htmlFor="price_per_cabin_unit">Cena za objekt (Kč/noc)</Label>
                              <Input
                                id="price_per_cabin_unit"
                                type="number"
                                placeholder="1000"
                                className="mt-1"
                                value={formData.price_per_cabin_unit}
                                onChange={(e) => setFormData({ ...formData, price_per_cabin_unit: e.target.value })}
                                data-testid="input-price_per_cabin_unit"
                              />
                              <p className="text-[11px] text-gray-500 mt-1">Pevná částka bez ohledu na počet osob.</p>
                            </div>
                          )}
                          <div>
                            <Label htmlFor="price_per_cabin">Cena za osobu (Kč/noc/osoba)</Label>
                            <Input
                              id="price_per_cabin"
                              type="number"
                              placeholder={formData.cabin_pricing_mode === 'combined' ? '150' : '300'}
                              className="mt-1"
                              value={formData.price_per_cabin}
                              onChange={(e) => setFormData({ ...formData, price_per_cabin: e.target.value })}
                              data-testid="input-price_per_cabin"
                            />
                            <p className="text-[11px] text-gray-500 mt-1">Vynásobí se počtem osob v chatě.</p>
                          </div>
                        </div>

                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 mt-3">
                          💡 <strong>Pozn.:</strong> Když host zvolí ubytování v chatě/maringotce/teepee, <strong>neúčtuje se cena za dospělého ani dítě</strong>.
                          {formData.cabin_pricing_mode === 'combined'
                            ? <> Účtuje se <em>(cena za objekt + počet osob × cena za osobu) × počet nocí</em>.</>
                            : <> Účtuje se <em>cena za osobu × počet osob × počet nocí</em>.</>
                          } Auto a stan se případně přičítají zvlášť.
                        </p>
                      </div>

                      <p className="col-span-2 text-xs text-gray-500">
                        Tip: Nevyplněné položky se neúčtují. Pokud chcete jen cenu za stan, vyplňte jen <em>Cena za stan</em>.
                      </p>
                    </div>
                    {errors.price && <p className="text-xs text-red-600 mt-1">⚠ {errors.price}</p>}
                  </div>
                )}

                <div className="mt-4" data-error={!!errors.maxCapacity}>
                  <Label htmlFor="maxCapacity">Maximální počet osob *</Label>
                  <div className="relative mt-1">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="maxCapacity"
                      type="number"
                      min="1"
                      placeholder="4"
                      className={`pl-10 ${errors.maxCapacity ? 'border-red-500 focus-visible:ring-red-400' : ''}`}
                      value={formData.maxCapacity}
                      onChange={(e) => { setFormData({ ...formData, maxCapacity: e.target.value }); if (parseInt(e.target.value) > 0) clearError('maxCapacity'); }}
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Hosté nebudou moci rezervovat pro více osob.</p>
                  {errors.maxCapacity && <p className="text-xs text-red-600 mt-1">⚠ {errors.maxCapacity}</p>}
                </div>
              </div>

              {/* Location Details */}
              <div data-error={!!errors.terrain}>
                <Label htmlFor="terrain">Typ terénu *</Label>
                <Select value={formData.terrain} onValueChange={(value) => { setFormData({ ...formData, terrain: value }); clearError('terrain'); }}>
                  <SelectTrigger className={`mt-1 ${errors.terrain ? 'border-red-500 ring-1 ring-red-400' : ''}`}>
                    <SelectValue placeholder="Vyberte terén" />
                  </SelectTrigger>
                  <SelectContent>
                    {terrains.map((terrain) => (
                      <SelectItem key={terrain.value} value={terrain.value}>
                        {terrain.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.terrain && <p className="text-xs text-red-600 mt-1">⚠ {errors.terrain}</p>}
              </div>

              {/* Caravans toggle – propagates to public listing as a badge */}
              <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <Checkbox
                  id="no_caravans"
                  checked={!!formData.no_caravans}
                  onCheckedChange={(v) => setFormData({ ...formData, no_caravans: !!v })}
                  data-testid="no-caravans-checkbox"
                />
                <Label htmlFor="no_caravans" className="cursor-pointer">
                  <span className="font-semibold">Majitel neakceptuje karavany</span>
                  <span className="block text-xs text-gray-600 mt-0.5">
                    Zobrazí se na kartě i v detailu místa jako upozornění pro hosty.
                  </span>
                </Label>
              </div>

              {/* Open access toggle + instructions */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                <Label className="font-semibold text-blue-900">Je pozemek volně přístupný?</Label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, open_access: true })}
                    className={`flex-1 px-4 py-2 rounded-md border-2 font-medium transition ${
                      formData.open_access === true
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
                    }`}
                    data-testid="open-access-yes"
                  >
                    ✓ ANO – volně přístupný
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, open_access: false })}
                    className={`flex-1 px-4 py-2 rounded-md border-2 font-medium transition ${
                      formData.open_access === false
                        ? 'bg-orange-600 text-white border-orange-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
                    }`}
                    data-testid="open-access-no"
                  >
                    ✗ NE – jsou potřeba pokyny
                  </button>
                </div>
                {formData.open_access === false && (
                  <div>
                    <Label htmlFor="open_access_instructions" className="text-sm">
                      Pokyny pro vstup (kód brány, kde si vyzvednout klíč, kontakt na souseda...)
                    </Label>
                    <Textarea
                      id="open_access_instructions"
                      placeholder="Např. Kód brány 1234*, klíč si vyzvedněte u souseda v čp. 12."
                      rows={3}
                      className="mt-1"
                      value={formData.open_access_instructions}
                      onChange={(e) => setFormData({ ...formData, open_access_instructions: e.target.value })}
                      data-testid="open-access-instructions"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Tyto pokyny <b>nejsou veřejné</b> – pošleme je hostovi v e-mailu po potvrzení rezervace.
                    </p>
                  </div>
                )}
              </div>

              {/* Campfire toggle (Yes / No) – with legal disclaimer */}
              <div
                className={`p-4 rounded-lg space-y-3 border ${errors.fire_allowed ? 'bg-red-50 border-red-400' : 'bg-amber-50 border-amber-200'}`}
                data-testid="fire-allowed-block"
                data-error={!!errors.fire_allowed}
              >
                <Label className="font-semibold text-amber-900">Je na pozemku povoleno rozdělání ohně? *</Label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, fire_allowed: true })}
                    className={`flex-1 px-4 py-2 rounded-md border-2 font-medium transition ${
                      formData.fire_allowed === true
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
                    }`}
                    data-testid="fire-allowed-yes"
                  >
                    ✓ ANO – oheň povolen
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, fire_allowed: false })}
                    className={`flex-1 px-4 py-2 rounded-md border-2 font-medium transition ${
                      formData.fire_allowed === false
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'
                    }`}
                    data-testid="fire-allowed-no"
                  >
                    ✗ NE – oheň zakázán
                  </button>
                </div>
                <p className="text-xs text-amber-800 leading-relaxed" data-testid="fire-allowed-disclaimer">
                  ⚠️ <b>Důležité upozornění:</b> Hosté si musí ověřit, zda není rozdělávání ohně v okamžik návštěvy regulováno státem (zákazem) – např. v období sucha, vyhlášenými zákazy hasičského záchranného sboru, na území národních parků a CHKO, případně dalších chráněných oblastí. Vy jako majitel pozemku dáváte pouze osobní souhlas – aktuální místní pravidla zůstávají na zodpovědnosti hosta.
                </p>
                {errors.fire_allowed && <p className="text-xs text-red-600">⚠ {errors.fire_allowed}</p>}
              </div>

              {/* Photo Upload */}
              <div data-error={!!errors.photos}>
                <Label htmlFor="photos">Fotografie místa *</Label>
                <div className="mt-2">
                  <label htmlFor="photo-upload" className={`flex items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${errors.photos ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-orange-500'}`}>
                    <div className="text-center">
                      <ImageIcon className={`w-8 h-8 mx-auto mb-2 ${errors.photos ? 'text-red-400' : 'text-gray-400'}`} />
                      <p className="text-sm text-gray-600">Klikněte pro nahrání fotek</p>
                      <p className="text-xs text-gray-500 mt-1">Maximálně 8 fotografií</p>
                    </div>
                    <input
                      id="photo-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => { handlePhotoUpload(e); if (e.target.files?.length) clearError('photos'); }}
                    />
                  </label>
                </div>
                {errors.photos && <p className="text-xs text-red-600 mt-1">⚠ {errors.photos}</p>}
                {photoFiles.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    {photoFiles.map((photo, index) => (
                      <div key={index} className="relative group">
                        <img src={photo} alt={`Preview ${index + 1}`} className="w-full h-24 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => handleRemovePhoto(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* NEW FIELDS SECTION */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold mb-4">Detaily prostředí</h3>
                
                {/* Mobile Signal & Tourist Traffic */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="mobile_signal">Mobilní signál *</Label>
                    <Select value={formData.mobile_signal} onValueChange={(value) => setFormData({ ...formData, mobile_signal: value })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Vyberte sílu signálu" />
                      </SelectTrigger>
                      <SelectContent>
                        {mobileSignalOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="tourist_traffic">Pohyb turistů v okolí *</Label>
                    <Select value={formData.tourist_traffic} onValueChange={(value) => setFormData({ ...formData, tourist_traffic: value })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Vyberte intenzitu" />
                      </SelectTrigger>
                      <SelectContent>
                        {touristTrafficOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Star Ratings */}
                <div className="space-y-4 mb-6">
                  <StarRating
                    id="stargazing"
                    label="Pozorování hvězd ⭐"
                    value={formData.stargazing_rating}
                    onChange={(value) => setFormData({ ...formData, stargazing_rating: value })}
                  />
                  
                  <StarRating
                    id="wildlife"
                    label="Pozorování zvěře 🦌"
                    value={formData.wildlife_rating}
                    onChange={(value) => setFormData({ ...formData, wildlife_rating: value })}
                  />
                  
                  <StarRating
                    id="privacy"
                    label="Míra samoty 🏕️"
                    value={formData.privacy_rating}
                    onChange={(value) => setFormData({ ...formData, privacy_rating: value })}
                  />

                  <StarRating
                    id="view"
                    label="Výhled do přírody 🌄"
                    value={formData.view_rating}
                    onChange={(value) => setFormData({ ...formData, view_rating: value })}
                  />

                  <StarRating
                    id="sounds"
                    label="Zvuky přírody 🔊"
                    value={formData.sounds_rating}
                    onChange={(value) => setFormData({ ...formData, sounds_rating: value })}
                  />
                </div>
              </div>

              {/* Parking (Feb 2026) */}
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg" data-testid="parking-block">
                <Label className="font-semibold text-slate-900">🅿️ Parkování</Label>
                <p className="text-xs text-gray-600 mb-3 mt-1">Kde mohou hosté parkovat auto?</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { val: 'on_site', label: 'Přímo na pozemku', hint: 'Auto zajede až k místu stanu / chatky' },
                    { val: 'off_site', label: 'Mimo pozemek', hint: 'Nutno dojít pěšky (uveďte vzdálenost)' },
                    { val: 'none', label: 'Bez parkování', hint: 'Bez auta / MHD / hromadné parkoviště' },
                  ].map((opt) => (
                    <label
                      key={opt.val}
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        formData.parking_type === opt.val
                          ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200'
                          : 'border-gray-200 hover:border-orange-300 bg-white'
                      }`}
                      data-testid={`parking-mode-${opt.val}`}
                    >
                      <input
                        type="radio"
                        name="parking_type"
                        value={opt.val}
                        className="mr-2"
                        checked={formData.parking_type === opt.val}
                        onChange={() => setFormData({ ...formData, parking_type: opt.val })}
                      />
                      <span className="font-semibold text-sm">{opt.label}</span>
                      <p className="text-xs text-gray-500 mt-0.5 pl-6">{opt.hint}</p>
                    </label>
                  ))}
                </div>
                {formData.parking_type === 'off_site' && (
                  <div className="mt-3">
                    <Label htmlFor="parking_distance_m" className="text-sm">Vzdálenost od místa (v metrech)</Label>
                    <Input
                      id="parking_distance_m"
                      type="number"
                      min={1}
                      placeholder="např. 150"
                      className="mt-1 max-w-xs"
                      value={formData.parking_distance_m}
                      onChange={(e) => setFormData({ ...formData, parking_distance_m: e.target.value })}
                      data-testid="parking-distance-input"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">Kolik metrů budou hosté muset dojít od auta k pozemku.</p>
                  </div>
                )}
              </div>
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="check-times">
                <Label className="font-semibold text-emerald-900">Časy příjezdu a odjezdu</Label>
                <p className="text-xs text-gray-600 mb-3 mt-1">
                  Aby se hosté nepotkávali a vy jste měl(a) čas na kontrolu pozemku mezi rezervacemi.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="check_in_time" className="text-sm">Příjezd od</Label>
                    <Input
                      id="check_in_time"
                      type="time"
                      className="mt-1"
                      value={formData.check_in_time}
                      onChange={(e) => setFormData({ ...formData, check_in_time: e.target.value })}
                      data-testid="check-in-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="check_out_time" className="text-sm">Odjezd nejpozději do</Label>
                    <Input
                      id="check_out_time"
                      type="time"
                      className="mt-1"
                      value={formData.check_out_time}
                      onChange={(e) => setFormData({ ...formData, check_out_time: e.target.value })}
                      data-testid="check-out-input"
                    />
                  </div>
                </div>
              </div>

              {/* Nearby attractions – up to 5 */}
              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="nearby-attractions">
                <Label className="font-semibold text-indigo-900">Zajímavá místa v okolí</Label>
                <p className="text-xs text-gray-600 mb-3 mt-1">
                  Doporučte hostům, kam se v okolí podívat. Vyplňte název (max 150 znaků) a odkaz, např. na mapy. <b>Pole můžete nechat prázdná.</b>
                </p>
                <div className="space-y-3">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start">
                      <Input
                        type="text"
                        maxLength={150}
                        placeholder={`Tip #${i + 1} – název (např. „Pivovar Vyškov")`}
                        className="sm:col-span-6"
                        value={formData.nearby_attractions?.[i]?.title || ''}
                        onChange={(e) => {
                          const arr = [...(formData.nearby_attractions || [])];
                          while (arr.length <= i) arr.push({ title: '', url: '' });
                          arr[i] = { ...arr[i], title: e.target.value };
                          setFormData({ ...formData, nearby_attractions: arr });
                        }}
                        data-testid={`nearby-title-${i}`}
                      />
                      <Input
                        type="url"
                        placeholder="https://… (odkaz)"
                        className="sm:col-span-6"
                        value={formData.nearby_attractions?.[i]?.url || ''}
                        onChange={(e) => {
                          const arr = [...(formData.nearby_attractions || [])];
                          while (arr.length <= i) arr.push({ title: '', url: '' });
                          arr[i] = { ...arr[i], url: e.target.value };
                          setFormData({ ...formData, nearby_attractions: arr });
                        }}
                        data-testid={`nearby-url-${i}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Booking Mode */}
              <div className="border-t pt-6 mt-6" data-error={!!errors.instant_book}>
                <h3 className="text-lg font-semibold mb-4">Způsob rezervace *</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Vyberte, jak chcete přijímat rezervace na vašem pozemku.
                </p>

                <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${errors.instant_book ? 'ring-2 ring-red-400 rounded-xl p-2 bg-red-50' : ''}`}>
                  <label
                    className={`cursor-pointer border-2 rounded-xl p-5 transition-all hover:shadow-md ${
                      formData.instant_book === true
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="instant-book-option"
                  >
                    <input
                      type="radio"
                      name="instant_book"
                      checked={formData.instant_book === true}
                      onChange={() => { setFormData({ ...formData, instant_book: true }); clearError('instant_book'); }}
                      className="sr-only"
                    />
                    <div className="flex items-center gap-2 font-semibold mb-2">
                      <span className="text-2xl">⚡</span>
                      <span>Okamžitá rezervace</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Host rezervuje a hned zaplatí. Vy nemusíte nic potvrzovat.
                    </p>
                    <p className="text-xs text-orange-600 mt-2 font-medium">Doporučeno – rychlejší konverze</p>
                  </label>

                  <label
                    className={`cursor-pointer border-2 rounded-xl p-5 transition-all hover:shadow-md ${
                      formData.instant_book === false
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="approval-required-option"
                  >
                    <input
                      type="radio"
                      name="instant_book"
                      checked={formData.instant_book === false}
                      onChange={() => { setFormData({ ...formData, instant_book: false }); clearError('instant_book'); }}
                      className="sr-only"
                    />
                    <div className="flex items-center gap-2 font-semibold mb-2">
                      <span className="text-2xl">🔐</span>
                      <span>Vyžaduje schválení</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Žádost o rezervaci nejprve potvrdíte vy. Host platí až po vašem schválení.
                    </p>
                    <p className="text-xs text-gray-500 mt-2">⏰ Schválit musíte do 24 h, jinak se automaticky zruší.</p>
                  </label>
                </div>
                {errors.instant_book && <p className="text-xs text-red-600 mt-2">⚠ {errors.instant_book}</p>}
              </div>

              {/* Rules Section */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold mb-4">Pravidla pozemku</h3>
                
                <div className="space-y-4">
                  {/* Pets */}
                  <div>
                    <Label htmlFor="pets_allowed">Domácí mazlíčci</Label>
                    <Select 
                      value={formData.rules.pets_allowed} 
                      onValueChange={(value) => setFormData({ 
                        ...formData, 
                        rules: { ...formData.rules, pets_allowed: value }
                      })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ano">Ano</SelectItem>
                        <SelectItem value="ne">Ne</SelectItem>
                        <SelectItem value="za příplatek">Za příplatek</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Quiet Hours */}
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <Checkbox 
                        id="quiet_hours"
                        checked={formData.rules.quiet_hours}
                        onCheckedChange={(checked) => setFormData({ 
                          ...formData, 
                          rules: { ...formData.rules, quiet_hours: checked }
                        })}
                      />
                      <Label htmlFor="quiet_hours" className="cursor-pointer">
                        Tiché hodiny
                      </Label>
                    </div>
                    
                    {formData.rules.quiet_hours && (
                      <div className="grid grid-cols-2 gap-4 ml-6">
                        <div>
                          <Label htmlFor="quiet_from" className="text-sm">Od</Label>
                          <Input
                            id="quiet_from"
                            type="time"
                            value={formData.rules.quiet_hours_from}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              rules: { ...formData.rules, quiet_hours_from: e.target.value }
                            })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="quiet_to" className="text-sm">Do</Label>
                          <Input
                            id="quiet_to"
                            type="time"
                            value={formData.rules.quiet_hours_to}
                            onChange={(e) => setFormData({ 
                              ...formData, 
                              rules: { ...formData.rules, quiet_hours_to: e.target.value }
                            })}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Smoking & Alcohol */}
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="smoking"
                        checked={formData.rules.smoking_allowed}
                        onCheckedChange={(checked) => setFormData({ 
                          ...formData, 
                          rules: { ...formData.rules, smoking_allowed: checked }
                        })}
                      />
                      <Label htmlFor="smoking" className="cursor-pointer">
                        Kouření povoleno
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="alcohol"
                        checked={formData.rules.alcohol_allowed}
                        onCheckedChange={(checked) => setFormData({ 
                          ...formData, 
                          rules: { ...formData.rules, alcohol_allowed: checked }
                        })}
                      />
                      <Label htmlFor="alcohol" className="cursor-pointer">
                        Alkohol povolen
                      </Label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map Location Selector */}
              <div className="border-t pt-6 mt-6" data-error={!!errors.map}>
                <Label className="text-base font-semibold">Vyberte polohu na mapě *</Label>
                <p className="text-sm text-gray-600 mb-2">
                  Klikněte na mapu pro výběr přesné lokace vašeho pozemku
                </p>
                <div className={errors.map ? 'ring-2 ring-red-400 rounded-lg p-1 bg-red-50' : ''}>
                  <MapComponent
                    locations={hasUserPickedSpot ? [{
                      id: 'new',
                      latitude: formData.latitude,
                      longitude: formData.longitude,
                      name: formData.name || 'Vybrané místo',
                      price: formData.price || 0,
                      type: 'Nové místo'
                    }] : []}
                    center={[formData.latitude, formData.longitude]}
                    zoom={8}
                    height="400px"
                    clickable={true}
                    onLocationSelect={handleMapClick}
                  />
                </div>
                <div className="mt-2 flex items-center gap-2 text-sm" data-testid="map-coords">
                  <MapPin className={`w-4 h-4 ${hasUserPickedSpot ? 'text-green-600' : 'text-gray-400'}`} />
                  {hasUserPickedSpot ? (
                    <span className="text-green-700 font-medium">
                      ✓ Vybráno: {formData.latitude.toFixed(5)}, {formData.longitude.toFixed(5)}
                    </span>
                  ) : (
                    <span className="text-gray-500 italic">Zatím nevybráno – klikněte na mapu</span>
                  )}
                </div>
                {errors.map && <p className="text-xs text-red-600 mt-1">⚠ {errors.map}</p>}
              </div>

              {/* House rules */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold mb-2">Pravidla majitele</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Volná pravidla, která chcete, aby hosté dodržovali. Tento text se zobrazí přímo v detailu místa.
                </p>
                <MarkdownHint />
                <Textarea
                  placeholder="Např. **Žádný hluk po 22:00**, kouření jen na vyhrazeném místě, prosíme respektujte sousedy a hospodářská zvířata..."
                  rows={4}
                  value={formData.owner_rules}
                  onChange={(e) => setFormData({ ...formData, owner_rules: e.target.value })}
                  data-testid="owner-rules-input"
                />
              </div>

              {/* Arrival instructions */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold mb-2">Pokyny pro příjezd</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Tyto informace dostane host po potvrzení rezervace (i v emailu). Popište cestu, parkování, orientační body apod.
                </p>
                <MarkdownHint />
                <Textarea
                  placeholder="Sjeďte z hlavní silnice u kapličky, pokračujte 800 m polní cestou. **Auto nechte na malém parkovišti u lesa** – dál pokračujte pěšky..."
                  rows={4}
                  value={formData.arrival_instructions}
                  onChange={(e) => setFormData({ ...formData, arrival_instructions: e.target.value })}
                  data-testid="arrival-instructions-input"
                />

                <div className="mt-4">
                  <Label>Obrázky s mapkou / orientací (volitelné, max 3)</Label>
                  <label
                    htmlFor="arrival-image-upload"
                    className={`mt-2 flex items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 rounded-lg transition-colors ${
                      (formData.arrival_images?.length || 0) >= 3
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer hover:border-orange-500'
                    }`}
                  >
                    <div className="text-center">
                      <ImageIcon className="w-7 h-7 mx-auto text-gray-400 mb-1" />
                      <p className="text-sm text-gray-600">
                        {(formData.arrival_images?.length || 0) >= 3
                          ? 'Maximální počet obrázků dosažen'
                          : `Nahrajte mapku / screenshot trasy (${formData.arrival_images?.length || 0}/3)`}
                      </p>
                    </div>
                    <input
                      id="arrival-image-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handleArrivalImageUpload}
                      data-testid="arrival-image-upload"
                      disabled={(formData.arrival_images?.length || 0) >= 3}
                    />
                  </label>
                  {(formData.arrival_images?.length || 0) > 0 && (
                    <div className="mt-3 flex flex-wrap gap-3">
                      {formData.arrival_images.map((img, idx) => (
                        <div key={idx} className="relative">
                          <img
                            src={img}
                            alt={`Pokyny pro příjezd ${idx + 1}`}
                            className="w-40 h-28 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeArrivalImage(idx)}
                            className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                            aria-label="Odstranit obrázek"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Facilities */}
              <div data-error={!!errors.facilities}>
                <Label>Vybavení a možnosti *</Label>
                <div className={`flex gap-2 mt-1 ${errors.facilities ? 'ring-2 ring-red-400 rounded-md p-1 bg-red-50' : ''}`}>
                  <Input
                    type="text"
                    placeholder="Např. Oheň povolený, Pitná voda"
                    value={newFacility}
                    onChange={(e) => setNewFacility(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFacility())}
                  />
                  <Button
                    type="button"
                    onClick={handleAddFacility}
                    variant="outline"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {errors.facilities && <p className="text-xs text-red-600 mt-1">⚠ {errors.facilities}</p>}

                {formData.facilities.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.facilities.map((facility, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 bg-orange-100 text-orange-700 px-3 py-1 rounded-full"
                      >
                        <span className="text-sm">{facility}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFacility(index)}
                          className="hover:text-orange-900"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-6 text-lg"
              >
                <MapPin className="w-5 h-5 mr-2" />
                Přidat místo
              </Button>

              <p className="text-sm text-gray-500 text-center">
                Vaše místo bude zkontrolováno před zveřejněním
              </p>
            </form>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default AddLocation;
